// Captions Phase 1: pure transcript → phrase segmentation.
//
// Splits a word-level transcript ([{ text, start, end }] in source seconds)
// into caption phrases using two rules:
//   1. Gap split: when the silence between a word's end and the next word's
//      start exceeds the gap threshold, start a new phrase.
//   2. Soft word cap: a phrase never exceeds maxWords (default 5) — a
//      readability default, not a user-facing constraint.

// Sensitivity is the user-facing 0..1 "loose ↔ tight" control. Internally it
// maps to a gap threshold: loose (0) = 500ms (long sentences), tight (1) =
// 50ms (nearly every word becomes its own phrase). The default sensitivity
// is calibrated to land on ~300ms.
export const GAP_LOOSE_SEC = 0.5
export const GAP_TIGHT_SEC = 0.05
export const DEFAULT_CAPTION_SENSITIVITY = 0.444
export const DEFAULT_PHRASE_MAX_WORDS = 5

export function sensitivityToGapSec(sensitivity) {
  const s = Number(sensitivity)
  const safe = Number.isFinite(s) ? Math.max(0, Math.min(1, s)) : DEFAULT_CAPTION_SENSITIVITY
  return GAP_LOOSE_SEC + (GAP_TIGHT_SEC - GAP_LOOSE_SEC) * safe
}

// Returns [{ text, start, end, words }] in source seconds.
export function segmentTranscript(words, { gapSec, maxWords = DEFAULT_PHRASE_MAX_WORDS } = {}) {
  if (!Array.isArray(words) || !words.length) return []
  const gap = Number.isFinite(Number(gapSec)) ? Math.max(0, Number(gapSec)) : sensitivityToGapSec()
  const cap = Math.max(1, Math.round(Number(maxWords) || DEFAULT_PHRASE_MAX_WORDS))

  const phrases = []
  let current = null

  const flush = () => {
    if (!current || !current.words.length) return
    phrases.push({
      text: current.words.map((w) => w.text).join(' '),
      start: current.words[0].start,
      end: current.words[current.words.length - 1].end,
      words: current.words,
    })
    current = null
  }

  let prevEnd = null
  for (const raw of words) {
    const text = `${raw?.text || ''}`.trim()
    const start = Number(raw?.start)
    const end = Number(raw?.end)
    if (!text || !Number.isFinite(start) || !Number.isFinite(end)) continue

    const gapBefore = prevEnd === null ? 0 : start - prevEnd
    const gapSplit = current && gapBefore > gap
    const capSplit = current && current.words.length >= cap
    if (gapSplit || capSplit) flush()

    if (!current) current = { words: [] }
    current.words.push({ text, start, end })
    prevEnd = end
  }
  flush()

  return phrases
}
